import { inject, Injectable } from '@angular/core';
import {
  EMPTY,
  Observable,
  catchError,
  expand,
  from,
  map,
  reduce,
  shareReplay,
  tap,
  throwError,
} from 'rxjs';
import {
  SkillsApi,
  SkillList,
  GetSkillsRequest,
  UpdateSkill,
  CreateSkill,
  Skill,
} from '@tourfold/libraries/tf-api-client-public-v2';

@Injectable({
  providedIn: 'root',
})
export class SkillsService {
  private readonly skillsApi = inject(SkillsApi);
  private allSkillsCache$?: Observable<Skill[]>;

  getSkillsData(data: GetSkillsRequest): Observable<SkillList> {
    return from(
      this.skillsApi.getSkills({
        page: data.page ?? 0,
        size: data.size ?? 50,
        sort: data.sort,
        search: data.search,
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getAllSkills(): Observable<Skill[]> {
    // Backend caps page size at 100, so we fetch pages until exhausted. The result is
    // cached via shareReplay — only the first call pays the multi-page cost; the user
    // edit/create dialog doesn't block on it (fire-and-forget; options fill in async).
    // If a tenant ever has so many skills that this is still painful, swap the
    // multiSelect to server-side (onFilter) search against getSkills({ search }).
    this.allSkillsCache$ ??= this.getSkillsPage({ page: 0, size: 100, sort: ['name:asc'] }).pipe(
      expand((response) => {
        const currentPage = response.page?.number ?? 0;
        const totalPages = response.page?.totalPages ?? 0;
        const nextPage = currentPage + 1;

        return nextPage < totalPages
          ? this.getSkillsPage({ page: nextPage, size: 100, sort: ['name:asc'] })
          : EMPTY;
      }),
      reduce((items, response) => [...items, ...response.items], [] as Skill[]),
      map((skills) => [...skills].sort((a, b) => a.name.localeCompare(b.name))),
      catchError((error) => {
        this.allSkillsCache$ = undefined;
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.allSkillsCache$;
  }

  private getSkillsPage(data: GetSkillsRequest): Observable<SkillList> {
    return from(
      this.skillsApi.getSkills({
        page: data.page ?? 0,
        size: data.size ?? 50,
        sort: data.sort,
        search: data.search,
      }),
    );
  }

  updateSkill(skillId: string, updateSkill: UpdateSkill): Observable<Skill> {
    return from(
      this.skillsApi.updateSkill({
        skillId,
        updateSkill,
      })
    ).pipe(
      tap(() => {
        this.allSkillsCache$ = undefined;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  createSkill(createSkill: CreateSkill): Observable<Skill> {
    return from(
      this.skillsApi.createSkill({
        createSkill,
      })
    ).pipe(
      tap(() => {
        this.allSkillsCache$ = undefined;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  deleteSkill(skillId: string): Observable<void> {
    return from(
      this.skillsApi.deleteSkill({
        skillId,
      })
    ).pipe(
      tap(() => {
        this.allSkillsCache$ = undefined;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
